// PunchConfirmDialog.js — Confirmation dialog for punch in/out with GPS + optional photo
// Layout (per design spec):
//   Header: 'Confirm Punch In/Out' + close icon
//   Body:   human-readable location info bar (status line + geofence pill +
//           GPS-accuracy warning badge), then the optional photo capture target
//           ('Include photo' toggle → tap-to-capture / preview box)
//   Footer: Cancel (secondary) + Confirm (primary) — two standard actions only
import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  PhotoCamera as CameraIcon,
  LocationOn as LocationIcon,
  WarningAmber as WarningIcon,
  MyLocation as MyLocationIcon,
} from '@mui/icons-material';
import AppButton from './shared/AppButton';
import CameraCapture, { isCameraAvailable } from './shared/CameraCapture';
import { haversineM, isWithinAnyGeofence } from '../utils/attendanceCalculations';

// GPS accuracy thresholds (meters). Phone GPS is typically 5–20m; anything
// above POOR is unreliable for geofence decisions and must be flagged.
const ACCURACY_WARN_M = 100;   // weak signal
const ACCURACY_POOR_M = 1000;  // effectively useless (~1km+ range)

const formatDistance = (m) => {
  if (m == null) return null;
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${Math.round(m)} m`;
};

export default function PunchConfirmDialog({
  open, onClose, onConfirm, punchType = 'in', geofenceZones = [],
}) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [includePhoto, setIncludePhoto] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);

  const cameraAvailable = isCameraAvailable();

  // Single stable object URL per captured photo, revoked on change/unmount
  useEffect(() => {
    if (!photo) { setPhotoUrl(null); return undefined; }
    const url = URL.createObjectURL(photo);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  useEffect(() => {
    if (open) {
      setPhoto(null);
      setIncludePhoto(false);
      setGpsCoords(null);
      setGpsError(null);
      setSubmitting(false);
      if (navigator.geolocation) {
        setGpsLoading(true);
        navigator.geolocation.getCurrentPosition(
          (pos) => { setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }); setGpsLoading(false); },
          (err) => { setGpsError(err.message || 'GPS unavailable'); setGpsLoading(false); },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
        );
      } else { setGpsError('Geolocation not supported'); }
    }
  }, [open]);

  const geofenceStatus = React.useMemo(() => {
    if (!gpsCoords || !geofenceZones.length) return { distance: null, nearestZone: null, inside: null };
    let nearestDist = Infinity, nearestZone = null;
    for (const z of geofenceZones) {
      const d = haversineM(gpsCoords.lat, gpsCoords.lng, parseFloat(z.lat), parseFloat(z.lng));
      if (d !== null && d < nearestDist) { nearestDist = d; nearestZone = z; }
    }
    return { distance: nearestDist === Infinity ? null : Math.round(nearestDist), nearestZone, inside: isWithinAnyGeofence(gpsCoords.lat, gpsCoords.lng, geofenceZones) };
  }, [gpsCoords, geofenceZones]);

  const doConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(punchType, {
        photo: includePhoto ? (photo || null) : null,
        lat: gpsCoords?.lat || null,
        lng: gpsCoords?.lng || null,
        accuracy: gpsCoords?.accuracy || null,
      });
    } catch (err) { console.error('[PunchConfirm]', err); }
    finally { setSubmitting(false); }
  };

  const label = punchType === 'in' ? 'Punch In' : 'Punch Out';

  // ── Location bar content ────────────────────────────────────────────
  const accuracy = gpsCoords?.accuracy;
  const accuracyTooLow = accuracy != null && accuracy > ACCURACY_POOR_M;
  const accuracyWeak = accuracy != null && !accuracyTooLow && accuracy > ACCURACY_WARN_M;

  // Human-readable status line: "Inside Main Office · 45m away" /
  // "Outside geofence · 8.5 km away" / "No GPS signal"
  const statusLine = (() => {
    if (gpsLoading) return 'Finding your location…';
    if (gpsError || !gpsCoords) return gpsError || 'No GPS signal';
    const { inside, distance, nearestZone } = geofenceStatus;
    if (inside === null) return 'No geofence configured';
    if (inside) return `Inside ${nearestZone?.name || 'geofence'}${distance != null ? ` · ${formatDistance(distance)} from center` : ''}`;
    return `Outside geofence${distance != null ? ` · ${formatDistance(distance)} away` : ''}`;
  })();

  const statusColor = gpsLoading
    ? 'text.secondary'
    : (gpsError || !gpsCoords)
      ? 'error.main'
      : geofenceStatus.inside === true ? 'success.main' : 'warning.main';

  const geofencePill = (() => {
    if (gpsLoading || gpsError || !gpsCoords || geofenceStatus.inside === null) return null;
    const { inside, distance } = geofenceStatus;
    if (inside) {
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '6px', bgcolor: 'success.lighter', border: '1px solid', borderColor: 'success.main', color: 'success.main', fontSize: '0.75rem', fontWeight: 500 }}>
          <MyLocationIcon sx={{ fontSize: 13 }} />
          In zone
        </Box>
      );
    }
    // Outside — subtle red badge per spec (rose-50 bg, rose-700 text)
    const near = distance <= ((geofenceStatus.nearestZone?.radius_m || 200) * 2);
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '6px', bgcolor: 'error.lighter', border: '1px solid', borderColor: 'error.main', color: 'error.main', fontSize: '0.75rem', fontWeight: 500 }}>
        <LocationIcon sx={{ fontSize: 13 }} />
        {near ? `Near zone · ${formatDistance(distance)}` : `Out of zone · ${formatDistance(distance)}`}
      </Box>
    );
  })();

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>Confirm {label}</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {/* Location info bar — human-readable, no raw lat/long */}
          <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: '8px', p: 1.5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                {gpsLoading && <CircularProgress size={13} sx={{ flexShrink: 0 }} />}
                <Typography variant="caption" sx={{ color: statusColor, fontWeight: 500 }} noWrap>
                  {statusLine}
                </Typography>
              </Box>
              {geofencePill}
            </Box>
            {/* Accuracy warning — translates ±50000m into plain language */}
            {accuracyTooLow && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1, px: 1, py: 0.25, borderRadius: '6px', bgcolor: 'warning.lighter', border: '1px solid', borderColor: 'warning.main', color: 'warning.main', fontSize: '0.75rem', fontWeight: 500 }}>
                <WarningIcon sx={{ fontSize: 13 }} />
                Low GPS accuracy (~{accuracy >= 100000 ? '100+ km' : formatDistance(Math.round(accuracy))} range)
              </Box>
            )}
            {accuracyWeak && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1, px: 1, py: 0.25, borderRadius: '6px', bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.300', color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500 }}>
                <WarningIcon sx={{ fontSize: 13 }} />
                Weak GPS signal (±{Math.round(accuracy)}m)
              </Box>
            )}
          </Box>

          {/* Photo capture — unified capture/preview target */}
          {cameraAvailable ? (
            photo ? (
              <Box sx={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid', borderColor: 'grey.200' }}>
                <Box sx={{ position: 'relative' }}>
                  <img src={photoUrl} alt="Punch selfie" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }} />
                  <IconButton
                    size="small"
                    aria-label="Remove photo"
                    onClick={() => setPhoto(null)}
                    sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.55)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Photo attached</Typography>
                  <AppButton variant="ghost" size="small" onClick={() => setCameraOpen(true)}>Retake</AppButton>
                </Box>
              </Box>
            ) : (
              <Box>
                <FormControlLabel
                  control={<Checkbox size="small" checked={includePhoto} onChange={(e) => setIncludePhoto(e.target.checked)} />}
                  label={<Typography variant="body2" color="text.secondary">Include photo with punch</Typography>}
                  sx={{ mb: includePhoto ? 1 : 0 }}
                />
                {includePhoto && (
                  <Box
                    onClick={() => setCameraOpen(true)}
                    role="button"
                    tabIndex={0}
                    aria-label="Take photo"
                    sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      gap: 1, py: 3, cursor: 'pointer',
                      border: '1.5px dashed', borderColor: 'grey.300', borderRadius: '8px',
                      bgcolor: 'grey.50',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.lighter' },
                    }}
                  >
                    <CameraIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">Tap to take photo</Typography>
                  </Box>
                )}
              </Box>
            )
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'grey.100', gap: 1.5, justifyContent: 'flex-end' }}>
          <AppButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</AppButton>
          <AppButton
            variant={punchType === 'in' ? 'primary' : 'danger'}
            onClick={doConfirm}
            disabled={submitting || (includePhoto && cameraAvailable && !photo)}
          >
            {submitting ? 'Punching…' : `Confirm ${label}`}
          </AppButton>
        </DialogActions>
      </Dialog>
      <CameraCapture open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={(file) => { setPhoto(file); setCameraOpen(false); }} />
    </>
  );
}
